import React, { PropsWithChildren, CSSProperties } from 'react';

export interface ProgressCircleProps {
  progress: number;
  animate?: boolean;
  animationDuration?: string;
  showPercentage?: boolean;
  showPercentageSymbol?: boolean;
  progressColor?: string;
  bgColor?: string;
  fillColor?: string;
  textColor?: string;
  size?: string;
  lineWidth?: string;
  percentSpacing?: number;
  textStyle?: CSSProperties;
  roundedStroke?: boolean;
  responsive?: boolean;
  style?: CSSProperties;
  onAnimationEnd?(): void;
}

const radius = 175;
const diameter = Math.round(Math.PI * radius * 2);
const getOffset = (val = 0) =>
  Math.round(((100 - Math.min(val, 100)) / 100) * diameter);

/**
 * .
 */
export default function ProgressCircle({
  progress = 0,
  animate = true,
  animationDuration = '1s',
  showPercentage = true,
  showPercentageSymbol = true,
  progressColor = '#2079df',
  bgColor = '#ecedf0',
  textColor = '#6b778c',
  fillColor = 'none',
  size = '100',
  lineWidth = '25',
  percentSpacing = 10,
  roundedStroke,
  responsive,
  onAnimationEnd,
  textStyle = { font: 'bold 4rem Helvetica, Arial, sans-serif' },
  style,
  children,
}: PropsWithChildren<ProgressCircleProps>): JSX.Element {
  const renderTextOrChildren = () => {
    if (children) return children;
    if (!showPercentage) return undefined;

    return (
      <text
        style={textStyle}
        fill={textColor}
        x={radius}
        y={radius}
        textAnchor="middle"
        dominantBaseline="central"
      >
        {progress}
        {/* eslint-disable-next-line formatjs/no-literal-string-in-jsx */}
        {showPercentageSymbol && <tspan dx={percentSpacing}>%</tspan>}
      </text>
    );
  };
  const strokeDashoffset = getOffset(progress);
  const transition = animate
    ? `stroke-dashoffset ${animationDuration} ease-out`
    : undefined;
  const strokeLinecap = roundedStroke ? 'round' : 'butt';
  const svgSize = responsive ? '100%' : size;

  return (
    <svg
      width={svgSize}
      height={svgSize}
      viewBox="-25 -25 400 400"
      style={style}
    >
      <circle
        stroke={bgColor}
        cx="175"
        cy="175"
        r="175"
        strokeWidth={lineWidth}
        fill={fillColor}
      />
      <circle
        stroke={progressColor}
        transform="rotate(-90 175 175)"
        cx="175"
        cy="175"
        r="175"
        strokeDasharray="1100"
        strokeWidth={lineWidth}
        strokeDashoffset="1100"
        strokeLinecap={strokeLinecap}
        fill="none"
        style={{ strokeDashoffset, transition }}
        onTransitionEnd={onAnimationEnd}
      />
      {renderTextOrChildren()}
    </svg>
  );
}
